import { useEffect, useMemo, useRef, useState } from "react";
import { Check, ChevronDown, Info, MoreHorizontal } from "lucide-react";
import { usePage, type SectionProps } from "./context";
import { Band, Head, cn, rv, str } from "./ui";
import { ReportChart, toneDot } from "./reports-chart";
import { reportFilterGroups, reportHints, reportMeta, reportRanges, reportTypes } from "@/mocks/reports";
import { goAnchor } from "./app-nav";
import { useAuth } from "./auth";
import ReportAds from "./reports-ads";
import { locale, t } from "@/lib/i18n";

/* Окно «Отчеты»: список отчётов слева, период и фильтры, обзорная карточка с графиком и таблица разбивки.
   Отчёт «Откуда клиенты» — живой (reports-ads.tsx, данные с сервера), остальные пока макет: переключатель «Демо-данные»
   наполняет их график событиями. Вошедший сотрудник сразу видит живой отчёт. */

const LIVE_ADS = {
  id: "ads",
  label: t("Откуда клиенты"),
  icon: "ri-megaphone-line",
  title: t("Откуда клиенты: реклама, источники, каналы"),
  description: t(
    "Сколько новых контактов пришло с какой рекламы — по площадкам (Google Ads, Meta и другие) и кампаниям, из какого источника Zoho CRM и по какому каналу они впервые обратились. По дням, неделям или месяцам за любой период; выгрузка в CSV.",
  ),
};

const RAIL = [
  { id: "reports", icon: "ri-bar-chart-2-line", label: t("Отчеты"), anchor: "#reports", active: true },
  { id: "workflows", icon: "ri-flow-chart", label: t("Рабочие процессы"), anchor: "#workflows", active: false },
  { id: "broadcasts", icon: "ri-mail-send-line", label: t("Рассылки"), anchor: "#broadcasts", active: false },
  { id: "inbox", icon: "ri-inbox-line", label: t("Входящие"), anchor: "#inbox-app", active: false },
  { id: "contacts", icon: "ri-contacts-book-2-line", label: t("Контакты"), anchor: "#contacts-app", active: false },
  { id: "dashboard", icon: "ri-dashboard-line", label: t("Дашборд"), anchor: "#dashboard", active: false },
  { id: "channels", icon: "ri-share-forward-line", label: t("Каналы"), anchor: "#channels", active: false },
  { id: "ai-assist", icon: "ri-sparkling-line", label: "AI Assist", anchor: "#ai-assist", active: false },
  { id: "ai-agents", icon: "ri-robot-2-line", label: t("AI-агенты"), anchor: "#ai-agents", active: false },
  { id: "lifecycle", icon: "ri-refresh-line", label: t("Жизненный цикл"), anchor: "#lifecycle", active: false },
  { id: "settings", icon: "ri-settings-3-line", label: t("Настройки"), anchor: "#settings", active: false },
];

const TODAY = new Date(2026, 9, 29);
const MONTHS = [t("января"), t("февраля"), t("марта"), t("апреля"), t("мая"), t("июня"), t("июля"), t("августа"), t("сентября"), t("октября"), t("ноября"), t("декабря")];
const MONTHS_SHORT = [t("янв"), t("фев"), t("мар"), t("апр"), t("мая"), t("июн"), t("июл"), t("авг"), t("сен"), t("окт"), t("ноя"), t("дек")];

function rangeDates(id: string): Date[] {
  if (id === "month") {
    const list: Date[] = [];
    for (let d = 1; d <= 31; d += 1) list.push(new Date(2026, 9, d));
    return list;
  }
  const back = id === "7" ? 6 : id === "30" ? 29 : 13;
  const list: Date[] = [];
  for (let k = back; k >= 0; k -= 1) {
    const d = new Date(TODAY);
    d.setDate(TODAY.getDate() - k);
    list.push(d);
  }
  return list;
}

const fmtFull = (d: Date) => t("{day} {month} {year} г.", { day: d.getDate(), month: MONTHS[d.getMonth()], year: d.getFullYear() });
const fmtShort = (d: Date) => `${d.getDate()} ${MONTHS_SHORT[d.getMonth()]}`;
const rangeLabel = (days: Date[]) => `${fmtFull(days[0])} - ${fmtFull(days[days.length - 1])}`;

/** Стабильный «шум» для столбцов: без Math.random, чтобы цифры не прыгали при перерисовке. */
function dayValue(seed: number, i: number, j: number) {
  const raw = Math.abs(Math.sin(seed * 0.37 + i * 1.63 + j * 2.71));
  const base = raw * 8.4 + 0.6;
  return Math.max(0, Math.round(j === 1 ? base * 0.42 : base));
}

const pct = (value: number, total: number) =>
  `${(total > 0 ? (value / total) * 100 : 0).toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`;

function ReportsDemo({ title, subtitle }: SectionProps) {
  const { brand } = usePage();
  const { user } = useAuth();
  const [reportId, setReportId] = useState(user ? LIVE_ADS.id : "broadcasts");
  const [range, setRange] = useState("14");
  const [demo, setDemo] = useState(false);
  const [filters, setFilters] = useState<{ group: string; value: string }[]>([]);
  const [rangeMenu, setRangeMenu] = useState(false);
  const [filterMenu, setFilterMenu] = useState(false);
  const [cardMenu, setCardMenu] = useState(false);
  const [infoOpen, setInfoOpen] = useState(false);
  const [updated, setUpdated] = useState(t("прямо сейчас"));
  const [refreshing, setRefreshing] = useState(false);
  const [toast, setToast] = useState("");
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const clock = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const rangeWrap = useRef<HTMLDivElement | null>(null);
  const filterWrap = useRef<HTMLDivElement | null>(null);
  const cardWrap = useRef<HTMLDivElement | null>(null);

  useEffect(
    () => () => {
      clearTimeout(timer.current);
      clearTimeout(clock.current);
    },
    [],
  );

  useEffect(() => {
    if (!rangeMenu && !filterMenu && !cardMenu && !infoOpen) return;
    const onDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (rangeWrap.current && !rangeWrap.current.contains(target)) setRangeMenu(false);
      if (filterWrap.current && !filterWrap.current.contains(target)) setFilterMenu(false);
      if (cardWrap.current && !cardWrap.current.contains(target)) setCardMenu(false);
      if (infoOpen && !(target instanceof Element && target.closest("[data-info-wrap]"))) setInfoOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [rangeMenu, filterMenu, cardMenu, infoOpen]);

  const flash = (text: string) => {
    setToast(text);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setToast(""), 4600);
  };

  const live = reportId === LIVE_ADS.id;
  const report = reportTypes.find((r) => r.id === reportId) ?? reportTypes[reportTypes.length - 1];
  const list = [LIVE_ADS, ...reportTypes];
  const days = useMemo(() => rangeDates(range), [range]);
  const labels = days.map(fmtShort);
  const fullLabels = days.map(fmtFull);

  const factor = !demo || filters.length >= 3 ? 0 : Math.pow(0.45, filters.length);

  const series = useMemo(
    () =>
      report.series.map((s, j) => ({
        label: s.label,
        tone: s.tone,
        values: days.map((_, i) => Math.round(dayValue(report.seed, i, j) * factor)),
      })),
    [report, days, factor],
  );

  const totals = series.map((s) => s.values.reduce((a, b) => a + b, 0));
  const grand = totals.reduce((a, b) => a + b, 0);
  const empty = grand === 0;
  const emptyText = filters.length > 0 ? reportMeta.emptyFiltered : reportMeta.emptyPlain;

  const scale = (days.length / 14) * factor;
  const rows = report.rows.map((r) => {
    const a = Math.round(r.values[0] * scale);
    const b = Math.round(r.values[1] * scale);
    return { name: r.name, a, b, share: a + b > 0 ? pct(a, a + b) : "—" };
  });
  const rowsSum = rows.reduce((acc, r) => ({ a: acc.a + r.a, b: acc.b + r.b }), { a: 0, b: 0 });

  const go = (anchor: string, label: string) => {
    if (goAnchor(anchor)) return;
    flash(t("Раздел «{label}» в демо не открывается", { label }));
  };

  const refresh = () => {
    if (refreshing) return;
    setRefreshing(true);
    clock.current = setTimeout(() => {
      setRefreshing(false);
      setUpdated(t("сегодня в {time}", { time: new Date().toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" }) }));
      flash(t("Отчёт «{report}» пересчитан за выбранный период", { report: report.label }));
    }, 850);
  };

  const pickReport = (id: string) => {
    setReportId(id);
    setFilters([]);
    setFilterMenu(false);
    const next = list.find((r) => r.id === id);
    if (next) flash(t("Открыт отчёт «{report}»", { report: next.label }));
  };

  const toggleFilter = (group: string, value: string) => {
    const exists = filters.some((f) => f.group === group && f.value === value);
    setFilters((prev) => (exists ? prev.filter((f) => !(f.group === group && f.value === value)) : [...prev, { group, value }]));
  };

  const toggleDemo = () => {
    const next = !demo;
    setDemo(next);
    setFilters([]);
    flash(next ? t("Демо-данные включены: в отчёте появились события за период") : t("Демо-данные выключены — цифры снова нулевые"));
  };

  return (
    <Band id="reports" tone="tint">
      <Head title={title} subtitle={subtitle} />
      <div {...rv()} className="flash-card relative overflow-hidden rounded-card border border-border bg-card !p-0">
        {/* полоска окна */}
        <div className="flex flex-wrap items-center gap-2.5 border-b border-border px-4 py-3">
          <span className="flex gap-1.5" aria-hidden>
            <span className="size-2.5 rounded-full bg-foreground/20" />
            <span className="size-2.5 rounded-full bg-foreground/20" />
            <span className="size-2.5 rounded-full bg-foreground/20" />
          </span>
          <span className="flash-mono ms-1 truncate text-[11px] text-muted-foreground">{str(brand) || "Hub"} / {t("рабочее пространство → отчеты")}</span>
          <span className="ms-auto flex items-center gap-2.5">
            <span className="hidden items-center gap-1.5 text-[10.5px] text-muted-foreground sm:inline-flex">
              <i className="ri-time-line text-[12px] leading-none" aria-hidden />
              {updated}
            </span>
            <span className="flex items-center gap-1.5">
              <span className="hidden text-[10.5px] font-semibold text-muted-foreground sm:inline">{t("Демо-данные")}</span>
              <button
                type="button"
                role="switch"
                aria-checked={demo}
                aria-label={t("Демо-данные")}
                onClick={toggleDemo}
                className={cn("relative h-5 w-9 flex-none cursor-pointer rounded-full border transition", demo ? "border-primary bg-primary" : "border-border bg-foreground/10")}
              >
                <span className={cn("absolute top-1/2 size-3.5 -translate-y-1/2 rounded-full bg-card transition-all", demo ? "start-[1.15rem]" : "start-[0.15rem]")} aria-hidden />
              </button>
            </span>
          </span>
        </div>

        {/* пробный период */}
        <div className="flex flex-wrap items-center gap-2 border-b border-border bg-primary/[.08] px-3.5 py-2 text-[11.5px]">
          <i className="ri-information-line text-[13px] leading-none text-primary" aria-hidden />
          <span className="min-w-0 flex-1 leading-relaxed text-foreground/80">
            {t("Пробный период Growth Plan закончится 3 ноября 2026 — через 6 дней. Обновите тариф, чтобы не потерять доступ.")}
          </span>
          <button
            type="button"
            onClick={() => go("#onboarding", t("Начало работы"))}
            className="inline-flex h-6 flex-none cursor-pointer items-center whitespace-nowrap rounded-full border border-primary/30 px-2.5 text-[10.5px] font-semibold text-primary transition hover:bg-primary/10"
          >
            {t("Чеклист онбординга")}
          </button>
          <button
            type="button"
            onClick={() => flash(t("Демо-режим: смена тарифа здесь недоступна"))}
            className="inline-flex h-6 flex-none cursor-pointer items-center whitespace-nowrap rounded-full bg-primary px-2.5 text-[10.5px] font-semibold text-primary-foreground transition hover:brightness-110"
          >
            {t("Обновить тариф")}
          </button>
        </div>

        <div className="flex">
          {/* иконная панель */}
          <div className="hidden w-12 flex-none flex-col items-center gap-1 border-e border-border py-3 lg:flex">
            {RAIL.map((r) => (
              <button
                key={r.id}
                type="button"
                onClick={() => (r.active ? undefined : go(r.anchor, r.label))}
                title={r.label}
                aria-label={r.label}
                className={cn(
                  "grid size-8 cursor-pointer place-items-center rounded-btn transition",
                  r.active ? "bg-primary/12 text-primary" : "text-muted-foreground hover:bg-muted hover:text-foreground",
                )}
              >
                <i className={cn(r.icon, "text-[15px] leading-none")} aria-hidden />
              </button>
            ))}
            <span className="mt-auto grid size-8 place-items-center rounded-full bg-primary text-[10px] font-bold text-primary-foreground" aria-hidden>
              {t("В")}
            </span>
          </div>

          {/* список отчётов */}
          <div className="hidden w-[10.5rem] flex-none border-e border-border py-2 md:block">
            <p className="flex items-center gap-1 px-3 py-1.5 text-[11px] font-semibold">{t("Отчеты")}</p>
            <ul className="flex flex-col gap-0.5 px-1.5">
              {list.map((r) => {
                const active = live ? r.id === LIVE_ADS.id : r.id === report.id;
                return (
                  <li key={r.id}>
                    <button
                      type="button"
                      onClick={() => pickReport(r.id)}
                      aria-current={active ? "page" : undefined}
                      className={cn(
                        "flex w-full cursor-pointer items-center gap-2 rounded-btn px-2 py-[5px] text-start text-[11.5px] transition",
                        active ? "bg-primary/10 font-semibold text-primary" : "text-foreground/75 hover:bg-muted hover:text-foreground",
                      )}
                    >
                      <i className={cn(r.icon, "flex-none text-[12.5px] leading-none")} aria-hidden />
                      <span className="truncate">{r.label}</span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>

          {/* рабочая область */}
          <div className="min-w-0 flex-1">
            {/* заголовок отчёта */}
            <div className="flex flex-wrap items-start justify-between gap-2 px-4 py-3">
              <div className="min-w-0">
                <h4 className="truncate text-[14px] font-semibold leading-snug">{live ? LIVE_ADS.title : report.title}</h4>
                <p className="mt-0.5 text-[10.5px] text-muted-foreground">
                  {live ? t("живые данные") : `${t("Отчёт за {n} дней", { n: days.length })} · ${filters.length > 0 ? t("{n} фильтр(а)", { n: filters.length }) : t("без фильтров")}`}
                </p>
              </div>
              <div className="flex flex-none items-start gap-2 text-end">
                <div className="text-[10px] leading-relaxed text-muted-foreground">
                  <p className="whitespace-nowrap">{t("Последнее обновление")} {updated}</p>
                  <p className="whitespace-nowrap">{t("Часовой пояс — {tz}", { tz: reportMeta.timeZone })}</p>
                </div>
                <button
                  type="button"
                  onClick={refresh}
                  aria-label={t("Обновить отчёт")}
                  title={t("Обновить отчёт")}
                  className="grid size-6 cursor-pointer place-items-center rounded-btn border border-border text-muted-foreground transition hover:border-primary/50 hover:text-primary"
                >
                  <i className={cn("ri-refresh-line text-[12px] leading-none", refreshing && "animate-spin")} aria-hidden />
                </button>
              </div>
            </div>

            <div className="border-t border-border px-4 py-2.5">
              <p className="max-w-3xl text-[11.5px] leading-relaxed text-muted-foreground">{live ? LIVE_ADS.description : report.description}</p>
            </div>

            {/* отчёты на мобильном — чипами */}
            <div className="flex gap-1.5 overflow-x-auto border-t border-border px-3 py-2 md:hidden">
              {list.map((r) => (
                <button
                  key={r.id}
                  type="button"
                  onClick={() => pickReport(r.id)}
                  className={cn(
                    "inline-flex flex-none cursor-pointer items-center gap-1 whitespace-nowrap rounded-full border px-2.5 py-1 text-[10.5px] transition",
                    (live ? r.id === LIVE_ADS.id : r.id === report.id) ? "border-primary/40 bg-primary/10 font-semibold text-primary" : "border-border text-foreground/70",
                  )}
                >
                  <i className={cn(r.icon, "text-[11px] leading-none")} aria-hidden />
                  {r.label}
                </button>
              ))}
            </div>

            {live ? (
              <div className="border-t border-border">
                <ReportAds />
              </div>
            ) : (
              <>
            {/* период и фильтры */}
            <div className="flex flex-wrap items-center gap-2 border-y border-border bg-background/40 px-4 py-2">
              <div className="relative" ref={rangeWrap}>
                <button
                  type="button"
                  onClick={() => setRangeMenu((v) => !v)}
                  aria-expanded={rangeMenu}
                  aria-haspopup="menu"
                  className="inline-flex h-7 cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-btn border border-border bg-card px-2.5 text-[11px] font-medium transition hover:border-primary/50 hover:text-primary"
                >
                  <i className="ri-calendar-line flex-none text-[12px] leading-none text-muted-foreground" aria-hidden />
                  {rangeLabel(days)}
                  <ChevronDown className="size-3 flex-none text-muted-foreground" aria-hidden />
                </button>
                {rangeMenu ? (
                  <div role="menu" className="absolute start-0 top-full z-30 mt-1 w-52 rounded-btn border border-border bg-card p-1 shadow-lift">
                    {reportRanges.map((r) => (
                      <button
                        key={r.id}
                        type="button"
                        role="menuitem"
                        onClick={() => {
                          setRange(r.id);
                          setRangeMenu(false);
                          flash(t("Период: {range}", { range: r.label.toLowerCase() }));
                        }}
                        className={cn(
                          "flex w-full cursor-pointer items-center justify-between gap-2 rounded-btn px-2 py-1.5 text-start text-[11.5px] transition hover:bg-muted",
                          range === r.id ? "font-semibold text-primary" : "text-foreground/80",
                        )}
                      >
                        {r.label}
                        {range === r.id ? <Check className="size-3" strokeWidth={3} aria-hidden /> : null}
                      </button>
                    ))}
                  </div>
                ) : null}
              </div>

              {filters.map((f) => (
                <span key={`${f.group}-${f.value}`} className="inline-flex h-7 items-center gap-1 rounded-full border border-primary/30 bg-primary/10 pe-1 ps-2.5 text-[10.5px] font-semibold text-primary">
                  {f.value}
                  <button
                    type="button"
                    onClick={() => setFilters((prev) => prev.filter((x) => !(x.group === f.group && x.value === f.value)))}
                    aria-label={t("Убрать фильтр {value}", { value: f.value })}
                    className="grid size-4 cursor-pointer place-items-center rounded-full transition hover:bg-primary/20"
                  >
                    <i className="ri-close-line text-[11px] leading-none" aria-hidden />
                  </button>
                </span>
              ))}

              <div className="relative flex items-center gap-2" ref={filterWrap}>
                <button
                  type="button"
                  onClick={() => setFilterMenu((v) => !v)}
                  aria-expanded={filterMenu}
                  aria-haspopup="menu"
                  className="inline-flex cursor-pointer items-center gap-1 whitespace-nowrap text-[11px] font-semibold text-foreground/75 transition hover:text-primary"
                >
                  <i className="ri-add-line text-[12px] leading-none" aria-hidden />
                  {t("Добавить фильтр")}
                </button>
                {filterMenu ? (
                  <div role="menu" className="absolute start-0 top-full z-30 mt-1 max-h-72 w-56 overflow-auto rounded-btn border border-border bg-card p-1 shadow-lift">
                    {reportFilterGroups.map((g) => (
                      <div key={g.id} className="py-0.5">
                        <p className="px-2 py-1 text-[9.5px] font-semibold uppercase tracking-wide text-muted-foreground">{g.label}</p>
                        {g.options.map((o) => {
                          const on = filters.some((f) => f.group === g.id && f.value === o);
                          return (
                            <button
                              key={o}
                              type="button"
                              role="menuitemcheckbox"
                              aria-checked={on}
                              onClick={() => toggleFilter(g.id, o)}
                              className="flex w-full cursor-pointer items-center justify-between gap-2 rounded-btn px-2 py-1.5 text-start text-[11.5px] text-foreground/80 transition hover:bg-muted hover:text-foreground"
                            >
                              {o}
                              {on ? <Check className="size-3 text-primary" strokeWidth={3} aria-hidden /> : null}
                            </button>
                          );
                        })}
                      </div>
                    ))}
                  </div>
                ) : null}
              </div>

              {filters.length > 0 ? (
                <button
                  type="button"
                  onClick={() => {
                    setFilters([]);
                    flash(t("Все фильтры сняты"));
                  }}
                  className="cursor-pointer whitespace-nowrap text-[11px] font-medium text-muted-foreground underline decoration-dotted underline-offset-2 transition hover:text-primary"
                >
                  {t("Очистить все")}
                </button>
              ) : null}

              {!demo ? (
                <button
                  type="button"
                  onClick={toggleDemo}
                  className="ms-auto inline-flex cursor-pointer items-center gap-1 whitespace-nowrap text-[10.5px] font-semibold text-primary underline decoration-dotted underline-offset-2"
                >
                  <i className="ri-play-circle-line text-[12px] leading-none" aria-hidden />
                  {t("Включить демо-данные")}
                </button>
              ) : null}
            </div>

            {/* обзор */}
            <div className="px-3.5 py-3">
              <div className="rounded-btn border border-border bg-card p-3.5" ref={cardWrap}>
                <div className="flex items-start justify-between gap-2">
                  <h5 className="flex items-center gap-1.5 text-[12px] font-semibold">
                    {report.overview}
                    <span className="relative inline-flex" data-info-wrap>
                      <button
                        type="button"
                        onClick={() => setInfoOpen((v) => !v)}
                        aria-label={reportMeta.info}
                        className="grid size-4 cursor-pointer place-items-center rounded-full text-muted-foreground transition hover:text-primary"
                      >
                        <Info className="size-3.5" aria-hidden />
                      </button>
                      {infoOpen ? (
                        <span className="absolute start-0 top-full z-30 mt-1 w-56 rounded-btn border border-border bg-card px-2.5 py-2 text-[10.5px] font-normal leading-relaxed text-muted-foreground shadow-lift">
                          {reportMeta.info}
                        </span>
                      ) : null}
                    </span>
                  </h5>
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => setCardMenu((v) => !v)}
                      aria-label={t("Ещё действия с отчётом")}
                      aria-expanded={cardMenu}
                      aria-haspopup="menu"
                      className="grid size-6 cursor-pointer place-items-center rounded-btn text-muted-foreground transition hover:bg-muted hover:text-foreground"
                    >
                      <MoreHorizontal className="size-4" aria-hidden />
                    </button>
                    {cardMenu ? (
                      <div role="menu" className="absolute end-0 top-full z-30 mt-1 w-52 rounded-btn border border-border bg-card p-1 shadow-lift">
                        {[
                          { label: t("Скачать CSV"), icon: "ri-download-2-line" },
                          { label: t("Скопировать ссылку"), icon: "ri-link" },
                          { label: t("Настроить отчёт"), icon: "ri-settings-3-line" },
                        ].map((m) => (
                          <button
                            key={m.label}
                            type="button"
                            role="menuitem"
                            onClick={() => {
                              setCardMenu(false);
                              flash(t("«{label}» в демо-режиме недоступен", { label: m.label }));
                            }}
                            className="flex w-full cursor-pointer items-center gap-2 rounded-btn px-2 py-1.5 text-start text-[11.5px] text-foreground/80 transition hover:bg-muted hover:text-foreground"
                          >
                            <i className={cn(m.icon, "text-[12px] leading-none text-muted-foreground")} aria-hidden />
                            {m.label}
                          </button>
                        ))}
                      </div>
                    ) : null}
                  </div>
                </div>

                <div className="mt-2.5 flex flex-wrap gap-x-12 gap-y-3.5">
                  {series.map((s, j) => (
                    <div key={s.label}>
                      <p className="text-[11px] text-muted-foreground">{report.metrics[j]}</p>
                      <p className="mt-1 text-[26px] font-semibold leading-none tabular-nums">{totals[j].toLocaleString(locale)}</p>
                      <p className={cn("mt-1 text-[10.5px] tabular-nums", s.tone === "ok" ? "text-emerald-600" : s.tone === "bad" ? "text-red-500" : "text-primary")}>
                        {pct(totals[j], grand)}
                      </p>
                    </div>
                  ))}
                </div>

                <div className="mt-3 flex flex-wrap items-center gap-4 border-t border-border/60 pt-2">
                  {series.map((s) => (
                    <span key={s.label} className="inline-flex items-center gap-1.5 text-[10.5px] text-muted-foreground">
                      <span className={cn("size-2 flex-none rounded-sm", toneDot[s.tone])} aria-hidden />
                      {s.label}
                    </span>
                  ))}
                  <span className="ms-auto inline-flex items-center gap-1 text-[10px] text-muted-foreground">
                    <i className="ri-information-line text-[11px] leading-none" aria-hidden />
                    {t("Учитывается время доставки")}
                  </span>
                </div>

                <div className="mt-3">
                  <ReportChart labels={labels} fullLabels={fullLabels} series={series} empty={empty} emptyText={emptyText} />
                </div>
              </div>

              {/* разбивка */}
              <div className="mt-3 overflow-hidden rounded-btn border border-border bg-card">
                <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border px-3.5 py-2">
                  <h5 className="text-[12px] font-semibold">{report.tableTitle}</h5>
                  <span className="text-[10px] text-muted-foreground">
                    {t("{n} дней", { n: days.length })} · {filters.length > 0 ? t("с фильтрами") : t("без фильтров")}
                  </span>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[26rem] border-collapse text-[11px]">
                    <thead>
                      <tr className="bg-background/40 text-[9.5px] uppercase tracking-wide text-muted-foreground">
                        <th scope="col" className="px-3.5 py-1.5 text-start font-semibold">{t("Группа")}</th>
                        <th scope="col" className="px-2 py-1.5 text-end font-semibold">{report.series[0].label}</th>
                        <th scope="col" className="px-2 py-1.5 text-end font-semibold">{report.series[1].label}</th>
                        <th scope="col" className="px-3.5 py-1.5 text-end font-semibold">{t("Доля")}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border/70">
                      {rows.map((r) => (
                        <tr key={r.name} className="transition hover:bg-muted/50">
                          <th scope="row" className="max-w-[12rem] truncate px-3.5 py-1.5 text-start font-medium">{r.name}</th>
                          <td className="px-2 py-1.5 text-end tabular-nums">{r.a}</td>
                          <td className="px-2 py-1.5 text-end tabular-nums text-muted-foreground">{r.b}</td>
                          <td className="px-3.5 py-1.5 text-end tabular-nums">{r.share}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="border-t border-border bg-background/40 font-semibold">
                        <td className="px-3.5 py-1.5">{t("Всего")}</td>
                        <td className="px-2 py-1.5 text-end tabular-nums">{rowsSum.a.toLocaleString(locale)}</td>
                        <td className="px-2 py-1.5 text-end tabular-nums">{rowsSum.b.toLocaleString(locale)}</td>
                        <td className="px-3.5 py-1.5 text-end tabular-nums">{rowsSum.a + rowsSum.b > 0 ? pct(rowsSum.a, rowsSum.a + rowsSum.b) : "—"}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
                {empty ? (
                  <p className="border-t border-border px-3.5 py-2 text-[10.5px] text-muted-foreground">
                    {emptyText}
                  </p>
                ) : null}
              </div>

              <div className="mt-3 grid gap-2 sm:grid-cols-3">
                {reportHints.map((h) => (
                  <div key={h.title} className="rounded-btn border border-border bg-background/40 p-2.5">
                    <p className="flex items-center gap-1.5 text-[11.5px] font-semibold">
                      <i className={cn(h.icon, "text-[13px] leading-none text-primary")} aria-hidden />
                      {h.title}
                    </p>
                    <p className="mt-1 text-[10.5px] leading-relaxed text-muted-foreground">{h.text}</p>
                  </div>
                ))}
              </div>

              <p className="mt-2.5 text-[10.5px] text-muted-foreground">
                {t("Демо-режим: цифры пересчитываются только на этой странице и не сохраняются после перезагрузки.")}
              </p>
            </div>
              </>
            )}
          </div>
        </div>

        {toast ? (
          <div
            role="status"
            className="pointer-events-none absolute inset-x-3 bottom-3 z-40 mx-auto flex max-w-md items-start gap-2 rounded-btn border border-primary/40 bg-background/95 px-3.5 py-2.5 text-[12px] text-foreground shadow-lift backdrop-blur"
          >
            <Check className="mt-0.5 size-3.5 flex-none text-primary" strokeWidth={3} aria-hidden />
            {toast}
          </div>
        ) : null}
      </div>
    </Band>
  );
}

export default ReportsDemo;